import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  format,
  parseISO,
  startOfDay,
  endOfDay,
  isBefore,
  isAfter,
} from 'date-fns';
import {
  fetchAllNotifications,
  seenNotification,
  type NotificationListFilters,
} from 'src/services/notifications';
import { useTranslation } from 'react-i18next';

import './notification.css';
import { getUserInfo } from 'src/services/userServicer';

const dummyNotifications: any[] = [];

const SEARCH_DEBOUNCE_MS = 400;
const PAGE_WINDOW = 5;

function filterNotificationsLocal(
  items: any[],
  typeFilter: string,
  startDate: string,
  endDate: string
): any[] {
  let out = [...items];
  if (typeFilter) {
    const t = typeFilter.toLowerCase();
    out = out.filter((n) => String(n?.type ?? '').toLowerCase() === t);
  }
  if (startDate) {
    const from = startOfDay(parseISO(startDate));
    out = out.filter((n) => {
      try {
        const d = parseISO(n.createdAt);
        return !isBefore(d, from);
      } catch {
        return true;
      }
    });
  }
  if (endDate) {
    const to = endOfDay(parseISO(endDate));
    out = out.filter((n) => {
      try {
        const d = parseISO(n.createdAt);
        return !isAfter(d, to);
      } catch {
        return true;
      }
    });
  }
  return out;
}

function getVisiblePageNumbers(
  currentPage: number,
  totalPages: number,
  windowSize: number
): number[] {
  if (totalPages <= 0) return [];
  if (totalPages <= windowSize) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const half = Math.floor(windowSize / 2);
  let start = Math.max(0, currentPage - half);
  let end = Math.min(totalPages - 1, start + windowSize - 1);
  start = Math.max(0, end - windowSize + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

const Notification = () => {
  const { t } = useTranslation();

  const [allNotification, setAllNotification] = useState(dummyNotifications);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftType, setDraftType] = useState('');
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const perPage = 10;

  const apiFilters: NotificationListFilters = useMemo(
    () => ({
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    }),
    [typeFilter, startDate, endDate]
  );

  const handleNavigation = (_item: any) => {
    // Add navigation logic here
  };

  const fetchNotificationsFromBackend = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllNotifications(
        perPage,
        currentPage + 1,
        debouncedSearch,
        apiFilters
      );
      const raw = data.data?.notifications ?? [];
      const filtered = filterNotificationsLocal(
        raw,
        typeFilter,
        startDate,
        endDate
      );
      setAllNotification(filtered);
      const total = data.data?.totalCount ?? 0;
      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / perPage)));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setAllNotification([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, apiFilters, typeFilter, startDate, endDate]);

  const seeNotifications = async () => {
    try {
      const user = await getUserInfo();
      if (!user?.id) return;
      await seenNotification(user.id);
    } catch (error) {
      console.error('Error seeing notifications:', error);
    }
  };

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    fetchNotificationsFromBackend();
  }, [fetchNotificationsFromBackend]);

  useEffect(() => {
    seeNotifications();
  }, []);

  useEffect(() => {
    if (!filtersOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtersOpen]);

  const openFiltersPanel = useCallback(() => {
    setDraftType(typeFilter);
    setDraftStart(startDate);
    setDraftEnd(endDate);
    setFiltersOpen(true);
  }, [typeFilter, startDate, endDate]);

  const closeFiltersPanel = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const applyFiltersFromPanel = useCallback(() => {
    setTypeFilter(draftType);
    setStartDate(draftStart);
    setEndDate(draftEnd);
    setCurrentPage(0);
    setFiltersOpen(false);
  }, [draftType, draftStart, draftEnd]);

  const resetFiltersFromPanel = useCallback(() => {
    setDraftType('');
    setDraftStart('');
    setDraftEnd('');
    setTypeFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
    setFiltersOpen(false);
  }, []);

  const pageNumbers = useMemo(
    () => getVisiblePageNumbers(currentPage, totalPages, PAGE_WINDOW),
    [currentPage, totalPages]
  );

  const hasActiveFilters =
    Boolean(searchInput.trim()) ||
    Boolean(typeFilter) ||
    Boolean(startDate) ||
    Boolean(endDate);

  const hasAppliedPanelFilters =
    Boolean(typeFilter) || Boolean(startDate) || Boolean(endDate);

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setTypeFilter('');
    setStartDate('');
    setEndDate('');
    setDraftType('');
    setDraftStart('');
    setDraftEnd('');
    setCurrentPage(0);
  };

  return (
    <div
      className="container-fluid p-3 controls-catalog-main"
      style={{ paddingTop: '1.25rem', zoom: 0.9 }}
    >
      <div className="notification-page__container">
        <div className="notification-page__card notification-page__toolbar-card">
          <div className="notification-page__toolbar">
            <div className="notification-page__search-wrap notification-page__search-wrap--toolbar">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={String(t('notification.search'))}
                className="form-control notification-page__search notification-page__search--toolbar"
                aria-label={String(t('notification.search'))}
              />
            </div>
            <div className="notification-page__toolbar-actions">
              {hasActiveFilters && (
                <button
                  type="button"
                  className="notification-page__clear-filters notification-page__clear-filters--toolbar"
                  onClick={clearFilters}
                >
                  {t('notification.clearFilters')}
                </button>
              )}
              <button
                type="button"
                className={`notification-page__filter-trigger${filtersOpen || hasAppliedPanelFilters ? ' notification-page__filter-trigger--active' : ''}`}
                onClick={() => {
                  if (filtersOpen) closeFiltersPanel();
                  else openFiltersPanel();
                }}
                aria-expanded={filtersOpen}
                aria-controls="notification-filters-panel"
                aria-label={String(t('notification.filtersTitle'))}
              >
                <svg
                  className="notification-page__filter-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden
                >
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                {hasAppliedPanelFilters && (
                  <span className="notification-page__filter-trigger-badge" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div
          className={`notification-page__filter-backdrop${filtersOpen ? ' notification-page__filter-backdrop--open' : ''}`}
          onClick={closeFiltersPanel}
          aria-hidden={!filtersOpen}
        />
        <aside
          id="notification-filters-panel"
          className={`notification-page__filter-panel${filtersOpen ? ' notification-page__filter-panel--open' : ''}`}
          role="dialog"
          aria-modal={filtersOpen}
          aria-labelledby="notification-filters-title"
          aria-hidden={!filtersOpen}
        >
          <div className="notification-page__filter-panel-header">
            <h2
              id="notification-filters-title"
              className="notification-page__filter-panel-title"
            >
              {t('notification.filtersTitle')}
            </h2>
            <button
              type="button"
              className="notification-page__filter-panel-close"
              onClick={closeFiltersPanel}
              aria-label={String(t('notification.closeFilters'))}
            >
              ×
            </button>
          </div>
          <div className="notification-page__filter-panel-body">
            <div className="notification-page__filter-field">
              <label
                className="notification-page__filter-label"
                htmlFor="notification-filter-type"
              >
                {t('notification.type')}
              </label>
              <div className="notification-page__filter-select-wrap">
                <select
                  id="notification-filter-type"
                  className="form-select notification-page__filter-select"
                  value={draftType}
                  onChange={(e) => setDraftType(e.target.value)}
                >
                  <option value="">{t('notification.allTypes')}</option>
                  <option value="ticket">Ticket</option>
                  <option value="issue">Issue</option>
                  <option value="task">Task</option>
                </select>
              </div>
            </div>
            <div className="notification-page__filter-field">
              <label
                className="notification-page__filter-label"
                htmlFor="notification-filter-start"
              >
                {t('notification.startDate')}
              </label>
              <div className="notification-page__filter-select-wrap">
                <input
                  id="notification-filter-start"
                  type="date"
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  className="form-control notification-page__filter-date"
                />
              </div>
            </div>
            <div className="notification-page__filter-field">
              <label
                className="notification-page__filter-label"
                htmlFor="notification-filter-end"
              >
                {t('notification.endDate')}
              </label>
              <div className="notification-page__filter-select-wrap">
                <input
                  id="notification-filter-end"
                  type="date"
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  className="form-control notification-page__filter-date"
                />
              </div>
            </div>
          </div>
          <div className="notification-page__filter-panel-footer">
            <button
              type="button"
              className="notification-page__filter-btn notification-page__filter-btn--ghost"
              onClick={resetFiltersFromPanel}
            >
              {t('notification.resetFilters')}
            </button>
            <button
              type="button"
              className="notification-page__filter-btn notification-page__filter-btn--primary"
              onClick={applyFiltersFromPanel}
            >
              {t('notification.applyFilters')}
            </button>
          </div>
        </aside>

        <div className="notification-page__table-wrap">
          {loading ? (
            <div className="notification-page__loading">
              {t('notification.loading')}
            </div>
          ) : allNotification.length > 0 ? (
            <div className="notification-page__table-scroll">
              <table className="notification-page__table">
                <thead>
                  <tr>
                    <th>{t('notification.About')}</th>
                    <th>{t('notification.Message')}</th>
                    <th>{t('notification.At')}</th>
                  </tr>
                </thead>
                <tbody>
                  {allNotification.map((item, index) => (
                    <tr
                      key={item?.id ?? index}
                      className="notification-page__row"
                      onClick={() => handleNavigation(item)}
                    >
                      <td>
                        <span className="notification-page__cell-type">
                          {item.type}
                        </span>
                      </td>
                      <td>{item.message}</td>
                      <td>
                        {format(
                          new Date(item.createdAt),
                          "yyyy-MM-dd 'at' hh:mm a"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="notification-page__empty">
              {t('notification.noData')}
            </div>
          )}

          {!loading && allNotification.length > 0 && totalPages > 0 && (
            <nav
              className="notification-page__pagination"
              aria-label={String(t('notification.page'))}
            >
              <button
                type="button"
                className="notification-page__pagination-link notification-page__pagination-link--edge"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
                disabled={currentPage === 0}
              >
                {t('notification.previous')}
              </button>

              {pageNumbers.map((pageIndex) => (
                <button
                  key={pageIndex}
                  type="button"
                  className={
                    pageIndex === currentPage
                      ? 'notification-page__pagination-page notification-page__pagination-page--active'
                      : 'notification-page__pagination-page'
                  }
                  onClick={() => setCurrentPage(pageIndex)}
                  aria-current={pageIndex === currentPage ? 'page' : undefined}
                  aria-label={`${t('notification.page')} ${pageIndex + 1}`}
                >
                  {pageIndex + 1}
                </button>
              ))}

              <button
                type="button"
                className="notification-page__pagination-link notification-page__pagination-link--edge"
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages - 1))
                }
                disabled={currentPage >= totalPages - 1}
              >
                {t('notification.next')}
              </button>

              <span className="notification-page__pagination-meta">
                {t('notification.page')} {currentPage + 1} {t('notification.of')}{' '}
                {totalPages}
                {totalCount > 0 ? ` · ${totalCount}` : ''}
              </span>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notification;
